/** Canonical auth entry paths (path-based mode, not `?mode=`). */

export type AuthPageMode = "signin" | "signup" | "forgot";

export function authPathForMode(mode: AuthPageMode): string {
  if (mode === "signup") return "/auth/signup";
  if (mode === "forgot") return "/auth/forgot";
  return "/auth/signin";
}

export function authModeFromPathname(pathname: string): AuthPageMode {
  if (pathname === "/auth/signup" || pathname.startsWith("/auth/signup/")) return "signup";
  if (pathname === "/auth/forgot" || pathname.startsWith("/auth/forgot/")) return "forgot";
  return "signin";
}

type BuildAuthPathOptions = {
  mode?: AuthPageMode;
  redirect?: string | null;
  role?: string | null;
  partner?: string | null;
  ref?: string | null;
  /** Extra query params (e.g. oauth=terms). */
  extra?: Record<string, string | null | undefined>;
};

/** Build `/auth/signin|signup|forgot?...` preserving safe query params. */
export function buildAuthPath(options?: BuildAuthPathOptions): string {
  const mode = options?.mode ?? "signin";
  const params = new URLSearchParams();

  const redirect = options?.redirect?.trim();
  if (redirect && redirect.startsWith("/") && !redirect.startsWith("//")) {
    params.set("redirect", redirect);
  }
  if (options?.role?.trim()) params.set("role", options.role.trim());
  if (options?.partner?.trim()) params.set("partner", options.partner.trim());
  if (options?.ref?.trim()) params.set("ref", options.ref.trim());

  if (options?.extra) {
    for (const [key, value] of Object.entries(options.extra)) {
      if (value != null && String(value).trim()) params.set(key, String(value).trim());
    }
  }

  const qs = params.toString();
  return `${authPathForMode(mode)}${qs ? `?${qs}` : ""}`;
}

/** Keep current search params when switching auth page (drops legacy `mode`). */
export function buildAuthPathFromSearch(
  mode: AuthPageMode,
  searchParams: URLSearchParams,
): string {
  const next = new URLSearchParams(searchParams);
  next.delete("mode");
  const qs = next.toString();
  return `${authPathForMode(mode)}${qs ? `?${qs}` : ""}`;
}
